import { datadogRum } from '@datadog/browser-rum';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { atom, useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useEffect } from 'react';

import { useBroadcastReload } from 'common/hooks/useBroadcastReload';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useToast } from 'common/hooks/useToast';

/**
 * prevent multiple app update notifications in short time
 */
const NOTIFICATION_TIMEOUT = 1000 * 60 * 5; // 5 minutes

const persistedExpirationAtom = atomWithStorage<null | number>('notificationExpiration', null);

const notificationTimeoutAtom = atom(
  (get) => {
    const timestamp = get(persistedExpirationAtom);
    if (timestamp === null) return true;
    const now = new Date().getTime();
    return now > timestamp;
  },
  (_get, set, action: 'clear' | 'reset') => {
    if (action === 'clear') {
      set(persistedExpirationAtom, null);
    } else {
      const timestamp = new Date().getTime() + NOTIFICATION_TIMEOUT;
      set(persistedExpirationAtom, timestamp);
    }
  },
);

// make sure the APP_VERSION and package.json version returned by server is properly set
// otherwise the version will constantly cause new notifications because of the mismatch
function isValidVersion(version?: string): version is string {
  return typeof version === 'string' && version.length === 40;
}

export function isVersionMismatch(serverVersion?: string, appVersion?: string) {
  if (!isValidVersion(serverVersion) || !isValidVersion(appVersion)) return false;
  return serverVersion !== appVersion;
}

/**
 * Polls the server for the version number and shows an update notification
 * when the version number has changed on the server compared to the client.
 *
 * Make sure the version update message is only shown once for a version
 * do not show the message if the version is the same as the one in local storage and message was shown.
 *
 * When an active tab shows a notification, it will broadcast a message to other tabs to reload the page.
 */
export const UpdateNotification = () => {
  const f = useFormatMessage();
  const broadcastReload = useBroadcastReload();
  const { addToast } = useToast();
  const [notificationTimeoutExpired, setNotificationTimeout] = useAtom(notificationTimeoutAtom);

  const { data: serverVersion, isError } = useQuery<string>({
    queryKey: ['version'],
    queryFn: () =>
      axios.get<{ version: string }>('/version.json').then((response) => response.data?.version),
    refetchInterval: 60 * 1000,
    staleTime: 0,
    enabled: process.env.NODE_ENV === 'production',
  });

  useEffect(() => {
    if (
      import.meta.env.VITE_DISABLE_APP_UPDATE_NOTIFICATIONS === 'true' ||
      isError ||
      !notificationTimeoutExpired
    ) {
      return;
    }

    if (isVersionMismatch(serverVersion, __VERSION_HASH__)) {
      datadogRum.addAction('app-update-notification', {
        appVersion: __VERSION_HASH__,
        s3Version: serverVersion,
      });
      addToast({
        variant: 'info',
        autoClose: false,
        title: f('components.update_notification.title'),
        description: f('components.update_notification.description'),
        primaryButtonLabel: f('components.update_notification.reload'),
        onPrimaryButtonClick: () => window.location.reload(),
      });
      setNotificationTimeout('reset');
      broadcastReload();
    }
  }, [
    isError,
    serverVersion,
    addToast,
    f,
    notificationTimeoutExpired,
    setNotificationTimeout,
    broadcastReload,
  ]);

  return null;
};
